<script setup lang="ts">
import { useAuthStore } from '@stores/useAuthStore';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { OnfidoTokenResponse } from '@graphql/types/onfido';
import { ONFIDO_TOKEN_QUERY } from '@graphql/queries/onfido';
import { useQuery } from '@urql/vue';

interface Emits {
  (event: 'update:currentStep', value: number): void;

  (event: 'onfidoCompleted'): void;
}

const emit = defineEmits<Emits>();

const { user } = toRefs(useAuthStore());

const { executeQuery: fetchOnfido, fetching: isFetching } = useQuery<OnfidoTokenResponse>({
  query: ONFIDO_TOKEN_QUERY,
  variables: { input: { hash: user.value?.onfido_hash } },
  pause: true,
});

const sdkToken = ref<string | undefined>(undefined);
const workflowId = ref<string | undefined>(undefined);
const isOnfidoStarted = ref(false);

const startOnfido = async () => {
  const { data, error } = await fetchOnfido();

  if (error.value) {
    useToast({
      title: 'Er is iets misgegaan bij het ophalen van Onfido',
      message: 'Probeer het later opnieuw',
      theme: 'error',
    });
  } else if (data.value) {
    sdkToken.value = data.value.onfido?.sdk_token;
    workflowId.value = data.value.onfido?.workflow_id;
    isOnfidoStarted.value = true;
  }
};

const finishOnfido = async () => {
  useToast({
    title: 'Gelukt!',
    message: 'Onfido is succesvol afgerond',
    theme: 'success',
  });

  emit('onfidoCompleted');
};
</script>

<template>
  <h2 class="text-2xl">Online identificeren</h2>
  <div v-if="isFetching" class="grid place-items-center min-h-[400px]">
    <UIMogelijkLoader class="w-32 lg:w-40 h-fit" />
  </div>
  <UIOnfido
    v-else-if="isOnfidoStarted && sdkToken && workflowId"
    is-modal
    :sdk-token="sdkToken"
    :workflow-id="workflowId"
    @onfido:completed="finishOnfido"
  />
  <div v-else class="flex flex-col gap-y-8">
    <p>
      Houd uw paspoort of ID-bewijs bij de hand voor de digitale identificatie via Onfido.
    </p>
    <div class="flex justify-between items-center">
      <UIButton
        data-test-id="onfido-previous-button"
        theme="tertiary"
        @click="emit('update:currentStep', user?.terms_and_conditions_accepted ? 1 : 2)"
      >
        Vorige
      </UIButton>
      <UIButton
        data-test-id="start-onfido-button"
        @click="startOnfido"
      >
        Online identificeren
        <template #icon-end>
          <FontAwesomeIcon :icon="['fas', 'arrow-right']" size="1x" />
        </template>
      </UIButton>
    </div>
  </div>
</template>
